import { Alerta, Badge, Button, Card, Combobox, DataList, Desplegable, Dialog, GraficoTorta, PercentField, Row, Select, Spinner, Stack, TextField } from "@/components";
import type { AtrasadoDistribucion, ClaseFiduciante, Distribucion, ItemDistribucion } from "@/lib/api";
import { formatFecha } from "@/lib/format";
import {
  aTextoApi,
  datosTorta,
  formatParticipacion,
  mensajeIncompleta,
  OPCIONES_CLASE,
  resumenAtrasado,
  textoAtrasado,
} from "@/lib/participacion";
import { useParticipacionTab } from "./useParticipacionTab";

function TablaDistribucion({ d, etiqueta }: { d: Distribucion; etiqueta: string }) {
  const columnas = [
    { clave: "nombre", titulo: "Fiduciante", render: (i: ItemDistribucion) => i.nombre },
    {
      clave: "clase",
      titulo: "Clase",
      render: (i: ItemDistribucion) => <Badge tono={i.clase === "B" ? "alerta" : "neutro"}>{i.clase}</Badge>,
    },
    {
      clave: "porcentaje",
      titulo: "Participación",
      render: (i: ItemDistribucion) => <span className="tabular-nums">{formatParticipacion(i.porcentaje)}</span>,
    },
  ];
  const hayB = d.items.some((i) => i.clase === "B");
  return (
    <Stack espacio={2}>
      <DataList etiqueta={etiqueta} columnas={columnas} filas={d.items} claveFila={(i) => `${i.fiduciante_id}-${i.clase}`} />
      {hayB && (
        <p className="text-chico text-texto-suave">
          Clase A: {formatParticipacion(d.suma_a)} · Clase B: {formatParticipacion(d.suma_b)}
        </p>
      )}
    </Stack>
  );
}

/** Lo atrasado de un cambio: quién paga y quién cobra (Mati, 2026-10-18: todo cambio es retroactivo). */
function Atrasado({ a, titulo }: { a: AtrasadoDistribucion | null | undefined; titulo: string }) {
  const resumen = resumenAtrasado(a);
  if (!a || !resumen) return null;
  return (
    <Stack espacio={2}>
      <h3 className="font-semibold">{titulo}</h3>
      <p className="text-chico text-texto-suave">{resumen} Queda en las cuentas corrientes.</p>
      <ul aria-label={titulo} className="flex flex-col gap-1">
        {a.lineas.map((l) => (
          <li key={l.fiduciante_id} className={l.monto_usd.trim().startsWith("-") ? "text-peligro" : "text-exito"}>
            {textoAtrasado(l)}
          </li>
        ))}
      </ul>
    </Stack>
  );
}

export function ParticipacionTab() {
  const t = useParticipacionTab();
  const d = t.dialogo;
  const listo = !t.cargando && !t.errorCarga;

  const acciones = (dist: Distribucion) =>
    t.esAdmin && dist.editable ? (
      <Row espacio={2}>
        <Button variante="secundario" tamanio="sm" aria-label={`Editar la distribución del ${formatFecha(dist.vigente_desde)}`} onClick={() => t.abrirEditar(dist)}>
          Editar
        </Button>
        <Button variante="fantasma" tamanio="sm" aria-label={`Borrar la distribución del ${formatFecha(dist.vigente_desde)}`} onClick={() => t.abrirBorrar(dist)}>
          Borrar
        </Button>
      </Row>
    ) : null;

  const bloque = (dist: Distribucion) => (
    <Desplegable key={dist.id} titulo={`Vigente desde ${formatFecha(dist.vigente_desde)}`}>
      <Stack espacio={3}>
        {!dist.completa && (
          <div>
            <Badge tono="alerta">{mensajeIncompleta(dist)}</Badge>
          </div>
        )}
        {dist.nota && <p className="text-texto-suave">{dist.nota}</p>}
        {acciones(dist)}
        <TablaDistribucion d={dist} etiqueta={`Distribución desde ${formatFecha(dist.vigente_desde)}`} />
        <Atrasado a={dist.atrasado} titulo={`Lo atrasado del cambio del ${formatFecha(dist.vigente_desde)}`} />
      </Stack>
    </Desplegable>
  );

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Participación</h1>
        {t.esAdmin && t.hayDistribuciones && <Button onClick={t.abrirNueva}>Nueva distribución</Button>}
      </Row>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}

      {listo && !t.hayDistribuciones && (
        <Card>
          <Stack espacio={3}>
            <p className="text-texto-suave">Todavía no se cargó la distribución inicial.</p>
            {t.esAdmin && (
              <div>
                <Button onClick={t.abrirNueva}>Cargar la distribución inicial</Button>
              </div>
            )}
          </Stack>
        </Card>
      )}

      {listo && t.hayDistribuciones && !t.vigente && (
        <Card>
          <p className="text-texto-suave">Todavía no hay una distribución vigente hoy.</p>
        </Card>
      )}

      {listo && t.incompletas.length > 0 && (
        <Stack espacio={3}>
          <h2 className="text-titulo font-light tracking-tight">Incompletas</h2>
          <p className="text-chico text-texto-suave">Una distribución incompleta se guarda para seguir cargándola, pero no rige hasta que sume 100 %.</p>
          {t.incompletas.map(bloque)}
        </Stack>
      )}

      {listo && t.vigente && (
        <section aria-labelledby="titulo-vigente">
          <Stack espacio={4}>
            <Row justificar="entre" envolver espacio={3}>
              <Stack espacio={1}>
                <h2 id="titulo-vigente" className="text-titulo font-light tracking-tight">
                  Distribución vigente
                </h2>
                <p className="text-chico text-texto-suave">Vigente desde {formatFecha(t.vigente.vigente_desde)}</p>
                {t.vigente.nota && <p className="text-texto-suave">{t.vigente.nota}</p>}
              </Stack>
              {acciones(t.vigente)}
            </Row>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto] md:items-start">
              <TablaDistribucion d={t.vigente} etiqueta="Distribución vigente" />
              <Card>
                <GraficoTorta
                  etiqueta="Gráfico de la participación vigente"
                  datos={datosTorta(t.vigente.items)}
                  formatValor={(v) => formatParticipacion(aTextoApi(v))}
                />
              </Card>
            </div>
            <Atrasado a={t.vigente.atrasado} titulo={`Lo atrasado del cambio del ${formatFecha(t.vigente.vigente_desde)}`} />
          </Stack>
        </section>
      )}

      {listo && t.programadas.length > 0 && (
        <Stack espacio={3}>
          <h2 className="text-titulo font-light tracking-tight">Programadas</h2>
          {t.programadas.map(bloque)}
        </Stack>
      )}

      {listo && t.anteriores.length > 0 && (
        <Stack espacio={3}>
          <h2 className="text-titulo font-light tracking-tight">Historia</h2>
          {t.anteriores.map(bloque)}
        </Stack>
      )}

      <Dialog
        abierto={d?.tipo === "form"}
        onCambio={(abierto) => !abierto && t.cerrar()}
        titulo={d?.tipo === "form" && d.distribucion ? "Editar distribución" : "Nueva distribución"}
      >
        <form onSubmit={(e) => void t.guardar(e)} noValidate>
          <Stack espacio={4}>
            <TextField
              etiqueta="Vigente desde"
              placeholder="dd/mm/aaaa"
              inputMode="numeric"
              value={t.form.vigente_desde}
              onChange={(e) => t.cambiarCampo("vigente_desde", e.target.value)}
              error={t.errores.vigente_desde}
            />
            <TextField etiqueta="Nota" ayuda="Ej.: cesión de Ana a Beto" value={t.form.nota} onChange={(e) => t.cambiarCampo("nota", e.target.value)} />
            <p className="text-chico text-texto-suave">
              A y B ponen su % sobre el total: entre las dos clases suman 100 %. Los deptos de los B se asignan después.
            </p>

            {t.form.lineas.length > 0 && (
              <ul aria-label="Fiduciantes agregados" className="flex flex-col divide-y divide-borde rounded-control border border-borde">
                {t.form.lineas.map((l, i) => (
                  <li key={`${l.fiduciante_id}-${l.clase}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                    <span className="font-medium">{l.nombre}</span>
                    <Badge tono={l.clase === "B" ? "alerta" : "neutro"}>{l.clase}</Badge>
                    <span className="tabular-nums text-texto-suave">{t.textoLinea(l)}</span>
                    <Row espacio={1} className="ml-auto">
                      <Button
                        type="button"
                        variante="fantasma"
                        tamanio="sm"
                        aria-label={`Cambiar ${l.nombre} clase ${l.clase}`}
                        onClick={() => t.cambiarLinea(i)}
                      >
                        Cambiar
                      </Button>
                      <Button
                        type="button"
                        variante="fantasma"
                        tamanio="sm"
                        aria-label={`Quitar ${l.nombre} clase ${l.clase}`}
                        onClick={() => t.quitar(i)}
                      >
                        Quitar
                      </Button>
                    </Row>
                  </li>
                ))}
              </ul>
            )}

            <p aria-live="polite" className={t.repartidoTodo ? "font-medium text-exito" : "font-medium text-alerta"}>
              {t.textoRestante}
            </p>
            {t.textoClases && <p className="text-chico text-texto-suave">{t.textoClases}</p>}

            {t.hayFiduciantes ? (
              <fieldset aria-label="Agregar fiduciante" className="rounded-control border border-borde p-3" onKeyDown={t.teclaEnNueva}>
                <Stack espacio={3}>
                  {t.nueva.fiduciante_id === null ? (
                    <Stack espacio={1}>
                      <Combobox
                        etiqueta="Fiduciante"
                        opciones={t.opcionesFiduciantes}
                        onElegir={t.elegirFiduciante}
                        placeholder="Escribí para buscar"
                      />
                      {t.erroresNueva.fiduciante && <p className="text-chico text-peligro">{t.erroresNueva.fiduciante}</p>}
                    </Stack>
                  ) : (
                    <Stack espacio={1}>
                      <span className="text-chico font-medium">Fiduciante</span>
                      <Row espacio={2} envolver>
                        <span className="font-medium">{t.nombreNueva}</span>
                        <Button type="button" variante="fantasma" tamanio="sm" aria-label="Elegir otro fiduciante" onClick={t.soltarFiduciante}>
                          Cambiar
                        </Button>
                      </Row>
                      {t.erroresNueva.fiduciante && <p className="text-chico text-peligro">{t.erroresNueva.fiduciante}</p>}
                    </Stack>
                  )}
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[10rem_1fr] sm:items-start">
                    <Select
                      etiqueta="Clase"
                      opciones={OPCIONES_CLASE}
                      valor={t.nueva.clase}
                      onCambio={(v) => t.cambiarClaseNueva(v as ClaseFiduciante)}
                    />
                    <PercentField
                      etiqueta="Participación (%)"
                      valor={t.nueva.valor}
                      onCambio={t.cambiarValorNueva}
                      ayuda={t.textoQueda}
                      error={t.erroresNueva.valor}
                    />
                  </div>
                  <Row espacio={2} envolver>
                    <Button type="button" variante="secundario" onClick={t.agregar}>
                      Agregar
                    </Button>
                    {t.usarLoQueQueda && (
                      <Button type="button" variante="fantasma" onClick={t.usarLoQueQueda}>
                        Poner lo que queda
                      </Button>
                    )}
                  </Row>
                </Stack>
              </fieldset>
            ) : (
              <Alerta tono="alerta" texto="Cargá los fiduciantes primero (en Fideicomiso → Fiduciantes)." />
            )}

            {t.form.lineas.length > 0 && <Alerta texto={t.errores.suma} />}
            {t.sumaIncompleta && (
              <p className="text-chico text-texto-suave">Podés guardarla incompleta y terminarla después: no rige hasta que sume 100 %.</p>
            )}
            <Atrasado a={t.atrasadoPrevio} titulo="Al guardar, lo atrasado" />
            <Alerta texto={t.errorGuardar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardando} disabled={!t.puedeGuardar}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar distribución">
        {d?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>¿Borrar la distribución vigente desde el {formatFecha(d.distribucion.vigente_desde)}? No se puede deshacer.</p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
